// PublicRoute - Redirects authenticated users away from public pages (login, signup)
'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/hooks/useAuth'
import { appConfig } from '@/configs/app.config'

interface PublicRouteProps {
  children: React.ReactNode
}

export default function PublicRoute({ children }: PublicRouteProps) {
  const router = useRouter()
  const { isAuthenticated, isLoading, role } = useAuth()

  useEffect(() => {
    if (!isLoading && isAuthenticated && role) {
      // Redirect authenticated users to their role-specific entry path
      const entryPath = appConfig.entryPath[role] || appConfig.authenticatedEntryPath
      router.replace(entryPath)
    }
  }, [isAuthenticated, isLoading, role, router])

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // If authenticated, don't render children (redirect will happen)
  if (isAuthenticated) {
    return null
  }

  // Render public page for unauthenticated users
  return <>{children}</>
}
